import clsx from 'clsx';
import { PaletteUtils } from '@shared/utils';
import { Attachments } from '@shared/api';
import DownloadIcon from '@assets/icons/messages/download.svg';
import TxtIcon from '@assets/icons/messages/txt.svg';
import XlsIcon from '@assets/icons/messages/xls.svg';
import ZipIcon from '@assets/icons/messages/zip.svg';
import PdfIcon from '@assets/icons/messages/pdf.svg';
import CloseIcon from '@assets/icons/messages/close.svg';

export interface FileType {
  name?: string;
  fileName?: string;
  url?: string;
  size: number;
  blobData?: Blob;
}

interface MessageAttachmentProps {
  className?: string;
  file: File | FileType | Attachments;
  onRemove?: (file: File | FileType | Attachments) => void;
  onSave?: () => void;
}

function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes == 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function getFileType(file: File | FileType | Attachments): string | null {
  const name = ('name' in file && file.name) || ('originalFilename' in file && file.originalFilename) || null;

  const url = 'url' in file ? file.url : null;

  let type = url ? get(url) : null;
  if (!type && name) type = get(name);
  return type;

  function get(name: string): string | null {
    if (!name) return null;
    const upperName = name.toUpperCase();
    if (upperName.includes('.PDF')) return 'pdf';
    if (upperName.includes('.TXT')) return 'txt';
    if (upperName.includes('.ZIP') || upperName.includes('.RAR')) return 'zip';
    if (upperName.includes('.XLS') || upperName.includes('.XLSX')) return 'excel';
    if (upperName.includes('.JPG') || upperName.includes('.BMP') || upperName.includes('.GIF') || upperName.includes('.PNG')) return 'image';
    return null;
  }
}

const getIcon = (file: File | FileType | Attachments) => {
  const type = getFileType(file);
  if (type == 'pdf') return PdfIcon;
  if (type == 'txt') return TxtIcon;
  if (type == 'zip') return ZipIcon;
  if (type == 'excel') return XlsIcon;
  return null;
};

export const MessageAttachment = (props: MessageAttachmentProps) => {
  const handleRemove = () => props.onRemove?.(props.file);

  const Icon = getIcon(props.file);
  const removable = !props.onSave;
  return (
    <div
      className={clsx(
        'bg-inherit inline-block max-lg:block border border-[#27272b] rounded-[4px] max-w-[179px] mb-[20px] mr-[20px] p-[12px] relative whitespace-nowrap before:inline-block before:h-full before:align-middle',
        !!props.onSave && 'cursor-pointer',
        props.className,
      )}
      onClick={props.onSave}
    >
      <div className='bg-center bg-cover bg-no-repeat h-[38px] inline-block mr-[15px] align-middle w-[31px]'>
        {Icon && <Icon className={clsx(PaletteUtils.isBordierPalette && 'text-[#dc291c]')} />}
      </div>
      <div className='inline-block  text-[12px] font-[600] leading-[1.83] -tracking-[0.2px] max-w-[calc(100%-46px)] align-middle'>
        <div className='text-[#27272b] overflow-hidden text-ellipsis'>
          {('name' in props.file && props.file.name) || ('originalFilename' in props.file && props.file.originalFilename)}
        </div>
        <div className='text-[#6b6b70] overflow-hidden text-ellipsis'>
          {('size' in props.file && formatBytes(props.file.size, 2)) || ('fileSizeBytes' in props.file && formatBytes(props.file.fileSizeBytes, 2))}
        </div>
      </div>
      <div
        className={clsx(
          'bg-[#fcfcfc] cursor-pointer h-[18px] left-[100%] -ml-[13px] -mt-[1px] absolute top-0 w-[18px]',
          removable ? 'bg-inherit' : 'bg-inherit',
        )}
        onClick={handleRemove}
      >
        {removable && (
          <CloseIcon className={clsx('left-[50%] -ml-[8px] -mt-[10px] top-[50%] absolute', PaletteUtils.isBordierPalette && 'text-[#dc291c]')} />
        )}
        {!removable && (
          <DownloadIcon className={clsx('left-[50%] -ml-[8px] -mt-[10px] top-[50%] absolute', PaletteUtils.isBordierPalette && '!text-[#dc291c]')} />
        )}
      </div>
    </div>
  );
};
